import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { supabase } from '../../../lib/supabase';

const InvoiceForm = ({ isOpen, onClose, onSubmit }) => {
  if (!isOpen) return null;

  const [formData, setFormData] = useState({
    customerId: '',
    ticketId: '',
    quoteId: '',
    taxRate: 8.5,
    discountAmount: 0,
    dueDate: '',
    notes: '',
    items: []
  });

  const [customers, setCustomers] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [itemForm, setItemForm] = useState({
    productId: '',
    description: '',
    quantity: 1,
    unitPrice: 0,
    discountPercent: 0
  });

  useEffect(() => {
    fetchFormData();
  }, []);

  const fetchFormData = async () => {
    try {
      setLoading(true);

      // Fetch customers
      const { data: customersData } = await supabase
        ?.from('customers')
        ?.select('id, name, email')
        ?.eq('status', 'active')
        ?.order('name');

      setCustomers((customersData || [])?.map(c => ({
        value: c?.id,
        label: c?.name,
        description: c?.email
      })));

      // Fetch tickets
      const { data: ticketsData } = await supabase
        ?.from('tickets')
        ?.select('id, ticket_number, title')
        ?.order('created_at', { ascending: false })
        ?.limit(50);

      setTickets((ticketsData || [])?.map(t => ({
        value: t?.id,
        label: t?.ticket_number,
        description: t?.title
      })));

      // Fetch quotes
      const { data: quotesData } = await supabase
        ?.from('quotes')
        ?.select('id, quote_number')
        ?.order('created_at', { ascending: false })
        ?.limit(50);

      setQuotes((quotesData || [])?.map(q => ({
        value: q?.id,
        label: q?.quote_number
      })));

      // Fetch products
      const { data: productsData } = await supabase
        ?.from('products')
        ?.select('id, name, sku, price')
        ?.order('name');

      setProducts((productsData || [])?.map(p => ({
        value: p?.id,
        label: p?.name,
        description: `${p?.sku} - $${p?.price}`,
        price: p?.price
      })));
    } catch (error) {
      console.error('Error fetching form data:', error?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleProductSelect = (productId) => {
    const product = products?.find(p => p?.value === productId);
    if (product) {
      setItemForm(prev => ({
        ...prev,
        productId: productId,
        description: product?.label,
        unitPrice: product?.price || 0
      }));
    }
  };

  const handleAddItem = () => {
    if (!itemForm?.description || itemForm?.quantity <= 0 || itemForm?.unitPrice < 0) {
      alert('Please fill in all item fields correctly');
      return;
    }

    setFormData(prev => ({
      ...prev,
      items: [...prev?.items, { ...itemForm }]
    }));

    setItemForm({
      productId: '',
      description: '',
      quantity: 1,
      unitPrice: 0,
      discountPercent: 0
    });
  };

  const handleRemoveItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev?.items?.filter((_, i) => i !== index)
    }));
  };

  const calculateItemTotal = (item) => {
    const subtotal = item?.quantity * item?.unitPrice;
    return subtotal * (1 - item?.discountPercent / 100);
  };

  const calculateSubtotal = () => {
    return formData?.items?.reduce((sum, item) => sum + calculateItemTotal(item), 0);
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const afterDiscount = subtotal - (formData?.discountAmount || 0);
    const tax = afterDiscount * (formData?.taxRate / 100);
    return afterDiscount + tax;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();

    if (!formData?.customerId || formData?.items?.length === 0) {
      alert('Please select a customer and add at least one item');
      return;
    }

    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[2000] flex items-center justify-center p-4">
      <div className="bg-card rounded-xl shadow-elevation-5 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="FileText" size={20} color="var(--color-primary)" />
            </div>
            <h2 className="text-xl font-heading font-semibold text-foreground">Create New Invoice</h2>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted transition-smooth">
            <Icon name="X" size={20} color="var(--color-foreground)" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Customer & References */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Customer"
              placeholder="Select customer"
              required
              searchable
              options={customers}
              value={formData?.customerId}
              onChange={(value) => setFormData(prev => ({ ...prev, customerId: value }))}
              loading={loading}
            />
            <Select
              label="Related Ticket (Optional)"
              placeholder="Select ticket"
              searchable
              options={tickets}
              value={formData?.ticketId}
              onChange={(value) => setFormData(prev => ({ ...prev, ticketId: value }))}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Select
              label="Related Quote (Optional)"
              placeholder="Select quote"
              searchable
              options={quotes}
              value={formData?.quoteId}
              onChange={(value) => setFormData(prev => ({ ...prev, quoteId: value }))}
            />
            <Input
              type="date"
              label="Due Date"
              required
              value={formData?.dueDate}
              onChange={(e) => setFormData(prev => ({ ...prev, dueDate: e?.target?.value }))}
            />
            <Input
              type="number"
              label="Tax Rate (%)"
              min="0"
              step="0.01"
              value={formData?.taxRate}
              onChange={(e) => setFormData(prev => ({ ...prev, taxRate: parseFloat(e?.target?.value) || 0 }))}
            />
          </div>

          {/* Add Items Section */}
          <div className="border-t border-border pt-6">
            <h3 className="text-lg font-heading font-semibold text-foreground mb-4">Add Items</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <Select
                label="Product (Optional)"
                placeholder="Select product"
                searchable
                options={products}
                value={itemForm?.productId}
                onChange={handleProductSelect}
              />
              <Input
                label="Description"
                placeholder="Item description"
                required
                value={itemForm?.description}
                onChange={(e) => setItemForm(prev => ({ ...prev, description: e?.target?.value }))}
              />
              <Input
                type="number"
                label="Quantity"
                min="1"
                value={itemForm?.quantity}
                onChange={(e) => setItemForm(prev => ({ ...prev, quantity: parseInt(e?.target?.value) || 1 }))}
              />
              <Input
                type="number"
                label="Unit Price"
                min="0"
                step="0.01"
                value={itemForm?.unitPrice}
                onChange={(e) => setItemForm(prev => ({ ...prev, unitPrice: parseFloat(e?.target?.value) || 0 }))}
              />
              <Input
                type="number"
                label="Discount %"
                min="0"
                max="100"
                step="0.01"
                value={itemForm?.discountPercent}
                onChange={(e) => setItemForm(prev => ({ ...prev, discountPercent: parseFloat(e?.target?.value) || 0 }))}
              />
            </div>
            <div className="mt-4">
              <Button type="button" onClick={handleAddItem} iconName="Plus" iconPosition="left" variant="outline">
                Add Item
              </Button>
            </div>
          </div>

          {/* Items Table */}
          {formData?.items?.length > 0 && (
            <div className="border-t border-border pt-6">
              <h3 className="text-lg font-heading font-semibold text-foreground mb-4">Invoice Items</h3>
              <div className="bg-background rounded-lg border border-border overflow-hidden">
                <table className="w-full">
                  <thead className="bg-muted/50 border-b border-border">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Description</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Qty</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Unit Price</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Discount</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Total</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData?.items?.map((item, index) => (
                      <tr key={index} className="border-b border-border last:border-0">
                        <td className="px-4 py-3 text-sm text-foreground">{item?.description}</td>
                        <td className="px-4 py-3 text-sm text-foreground">{item?.quantity}</td>
                        <td className="px-4 py-3 text-sm text-foreground">${item?.unitPrice?.toFixed(2)}</td>
                        <td className="px-4 py-3 text-sm text-foreground">{item?.discountPercent}%</td>
                        <td className="px-4 py-3 text-sm font-medium text-foreground">${calculateItemTotal(item)?.toFixed(2)}</td>
                        <td className="px-4 py-3">
                          <Button type="button" variant="ghost" size="sm" onClick={() => handleRemoveItem(index)} iconName="Trash2" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Totals */}
          {formData?.items?.length > 0 && (
            <div className="border-t border-border pt-6">
              <div className="max-w-md ml-auto space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal:</span>
                  <span className="font-medium text-foreground">${calculateSubtotal()?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-muted-foreground">Discount:</span>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    className="w-32 text-right"
                    value={formData?.discountAmount}
                    onChange={(e) => setFormData(prev => ({ ...prev, discountAmount: parseFloat(e?.target?.value) || 0 }))}
                  />
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Tax ({formData?.taxRate}%):</span>
                  <span className="font-medium text-foreground">${((calculateSubtotal() - formData?.discountAmount) * (formData?.taxRate / 100))?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-lg font-bold border-t border-border pt-3">
                  <span className="text-foreground">Total:</span>
                  <span className="text-primary">${calculateTotal()?.toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Notes */}
          <Input
            label="Notes (Optional)"
            placeholder="Additional notes or payment terms"
            value={formData?.notes}
            onChange={(e) => setFormData(prev => ({ ...prev, notes: e?.target?.value }))}
          />

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-border">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" iconName="Save" iconPosition="left">
              Create Invoice
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InvoiceForm;